import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DOCUMENT, DatePipe } from '@angular/common';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';
import { PayloadService } from '../../services/payload.service';

@Component({
  selector: 'app-evento-detail',
  imports: [RouterLink, DatePipe],
  templateUrl: './evento-detail.component.html',
  styleUrl: './evento-detail.component.scss',
})
export class EventoDetailComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private payloadService = inject(PayloadService);
  private sanitizer = inject(DomSanitizer);
  private meta = inject(Meta);
  private title = inject(Title);
  private document = inject(DOCUMENT);

  evento: any = null;
  descrizioneHtml: SafeHtml | null = null;

  ngOnInit(): void {
    const year = this.route.snapshot.paramMap.get('year');
    const slug = this.route.snapshot.paramMap.get('slug');
    if (year && slug) {
      const fullSlug = `${year}/${slug}`;
      this.payloadService.getEventoBySlug(fullSlug).subscribe((res: any) => {
        if (res.docs?.length > 0) {
          this.evento = res.docs[0];
          if (this.evento.descrizioneHTML) {
            this.descrizioneHtml = this.sanitizer.bypassSecurityTrustHtml(
              this.evento.descrizioneHTML
            );
          }
          this.setSeoMeta();
          this.setJsonLd();
        }
      });
    }
  }

  showEndDate(): boolean {
    if (!this.evento?.dataFine) return false;
    return new Date(this.evento.dataInizio).toDateString() !== new Date(this.evento.dataFine).toDateString();
  }

  getMapsUrl(posizione: string): string {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(posizione)}`;
  }

  getImageUrl(media: any): string {
    if (typeof media === 'string') return media;
    return media?.url ?? '';
  }

  private getPlainDescription(): string {
    if (!this.evento.descrizioneHTML) return '';
    const text = this.evento.descrizioneHTML.replace(/<[^>]*>/g, '');
    return text.length > 160 ? text.substring(0, 157) + '...' : text;
  }

  private setSeoMeta(): void {
    const titolo = this.evento.titolo;
    const description = this.getPlainDescription() ||
      `${titolo} - Evento organizzato da Come Tralci`;
    const url = `https://cometralci.com/eventi/${this.evento.slug}`;
    const imageUrl = this.evento.immagineCopertina?.url || this.evento.immagineWide?.url || '';

    this.title.setTitle(`${titolo} - Come Tralci`);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });

    // Open Graph
    this.meta.updateTag({ property: 'og:title', content: `${titolo} - Come Tralci` });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:type', content: 'event' });
    this.meta.updateTag({ property: 'og:url', content: url });
    this.meta.updateTag({ property: 'og:site_name', content: 'Come Tralci' });
    this.meta.updateTag({ property: 'og:locale', content: 'it_IT' });
    if (imageUrl) {
      this.meta.updateTag({ property: 'og:image', content: imageUrl });
    }

    // Twitter Card
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: `${titolo} - Come Tralci` });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    if (imageUrl) {
      this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
    }

    this.updateCanonicalUrl(url);
  }

  private setJsonLd(): void {
    const existingScript = this.document.querySelector('script#evento-jsonld');
    if (existingScript) existingScript.remove();

    const jsonLd: any = {
      '@context': 'https://schema.org',
      '@type': 'Event',
      name: this.evento.titolo,
      startDate: this.evento.dataInizio,
      organizer: {
        '@type': 'Organization',
        name: 'Come Tralci',
        url: 'https://cometralci.com',
      },
      eventStatus: 'https://schema.org/EventScheduled',
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    };

    if (this.evento.dataFine) {
      jsonLd.endDate = this.evento.dataFine;
    }

    if (this.evento.posizione) {
      jsonLd.location = {
        '@type': 'Place',
        name: this.evento.posizione,
        address: {
          '@type': 'PostalAddress',
          name: this.evento.posizione,
        },
      };
    }

    const description = this.getPlainDescription();
    if (description) {
      jsonLd.description = description;
    }

    const imageUrl = this.evento.immagineCopertina?.url || this.evento.immagineWide?.url;
    if (imageUrl) {
      jsonLd.image = imageUrl;
    }

    jsonLd.url = `https://cometralci.com/eventi/${this.evento.slug}`;

    jsonLd.performer = {
      '@type': 'Organization',
      name: 'Come Tralci',
      url: 'https://cometralci.com',
    };

    jsonLd.offers = {
      '@type': 'Offer',
      url: jsonLd.url,
      price: '0',
      priceCurrency: 'EUR',
      validFrom: this.evento.createdAt || this.evento.dataInizio,
      availability: this.evento.iscrizioniAperte
        ? 'https://schema.org/InStock'
        : 'https://schema.org/SoldOut',
    };

    const script = this.document.createElement('script');
    script.id = 'evento-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    this.document.head.appendChild(script);
  }

  private updateCanonicalUrl(url: string): void {
    let link = this.document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (link) {
      link.href = url;
    } else {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      link.href = url;
      this.document.head.appendChild(link);
    }
  }
}
